import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { FC, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { InvoiceStatus } from '@libs/schemas/src/invoice';
import { Mail, Send, CreditCard, CheckCircle, MessageSquare } from 'lucide-react';

export const InvoiceDetailsActionMenu: FC<{
  invoiceStatus: InvoiceStatus;
  paymentIntentId?: string;
  isLoading?: boolean;
  onResendInvoice: () => void;
  onSendInvoice: () => void;
  onSendSMS: () => void;
  onMarkAsSent: () => void;
  onEditInvoice: () => void;
  onDeleteInvoice: () => void;
  onCancelInvoice: () => void;
  onReprocessPayment: () => void;
  onMarkAsPaid: () => void;
}> = ({
  invoiceStatus,
  paymentIntentId,
  onResendInvoice,
  onSendInvoice,
  onSendSMS,
  onMarkAsSent,
  onDeleteInvoice,
  onCancelInvoice,
  onReprocessPayment,
  onMarkAsPaid,
  isLoading,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNoActions, setShowNoActions] = useState(
    [InvoiceStatus.CANCELLED, InvoiceStatus.PAID].includes(invoiceStatus),
  );

  // Check if we should show reprocess payment option
  const shouldShowReprocessPayment =
    (invoiceStatus === InvoiceStatus.SENT || invoiceStatus === InvoiceStatus.PAYMENT_FAILED) &&
    paymentIntentId;

  // Check if we should show mark as paid option
  const shouldShowMarkAsPaid = [
    InvoiceStatus.DRAFT,
    InvoiceStatus.SENT,
    InvoiceStatus.PAYMENT_FAILED,
  ].includes(invoiceStatus);

  useEffect(() => {
    setShowNoActions([InvoiceStatus.CANCELLED, InvoiceStatus.PAID].includes(invoiceStatus));
  }, [invoiceStatus]);

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" disabled={isLoading}>
            Actions
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {!showNoActions && (
            <>
              <DropdownMenuLabel>Invoice Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}

          {showNoActions && (
            <DropdownMenuLabel className="text-muted">No actions available</DropdownMenuLabel>
          )}

          <DropdownMenuGroup>
            {invoiceStatus === InvoiceStatus.DRAFT && (
              <>
                <DropdownMenuItem className="py-4" onClick={onSendInvoice}>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Invoice By Email
                </DropdownMenuItem>
                <DropdownMenuItem className="py-4" onClick={onSendSMS}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Invoice By SMS
                </DropdownMenuItem>
              </>
            )}
            {[InvoiceStatus.DRAFT, InvoiceStatus.PAYMENT_FAILED].includes(invoiceStatus) && (
              <DropdownMenuItem className="py-4" onClick={onMarkAsSent}>
                <Send className="h-4 w-4 mr-2" />
                Mark as Sent
              </DropdownMenuItem>
            )}
            {invoiceStatus === InvoiceStatus.SENT && (
              <>
                <DropdownMenuItem className="py-4" onClick={onResendInvoice}>
                  <Mail className="h-4 w-4 mr-2" />
                  Resend Invoice E-mail
                </DropdownMenuItem>
                <DropdownMenuItem className="py-4" onClick={onSendSMS}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Invoice By SMS
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuGroup>

          {shouldShowReprocessPayment && (
            <DropdownMenuItem className="py-4" onClick={onReprocessPayment}>
              <CreditCard className="h-4 w-4 mr-2" />
              Reprocess Payment
            </DropdownMenuItem>
          )}

          {shouldShowMarkAsPaid && (
            <DropdownMenuItem className="py-4" onClick={onMarkAsPaid}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Invoice as Paid
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuLabel>
            {invoiceStatus === InvoiceStatus.CANCELLED && (
              <Button type="button" variant="destructive" onClick={onDeleteInvoice}>
                Delete Invoice
              </Button>
            )}

            {![InvoiceStatus.CANCELLED, InvoiceStatus.PAID].includes(invoiceStatus) && (
              <Button
                className="w-full"
                type="button"
                variant="destructive"
                onClick={onCancelInvoice}
              >
                Cancel Invoice
              </Button>
            )}
          </DropdownMenuLabel>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
